import type {
  DifferentiationQuestion,
  Option,
  PatternId,
  Situation,
} from "../content/types.js";
import type {
  Answer,
  AnswerTrace,
  ClassificationResult,
  ConsistencyLevel,
  ResultState,
} from "./engine.types.js";

export const ENGINE_VERSION = "0.1.0-alpha";

/**
 * No single option may weigh more than this toward any one pattern. This is
 * a content-validation constant (see content tests), not just documentation:
 * classify() cannot, by construction, let one answer decide the result when
 * every other constant below is a multiple of it.
 */
export const MAX_SINGLE_OPTION_WEIGHT = 3;

const MIN_VALID_ANSWERS = 3;
const MIN_LEADER_SCORE = 2;

/**
 * Cuántas SITUACIONES DISTINTAS tienen que aportar evidencia positiva al líder
 * para que se le adjudique a alguien un patrón.
 *
 * Sin esto, `MIN_LEADER_SCORE = 2` se alcanza con una sola respuesta de +2: una
 * persona que contesta siete preguntas y sólo coincide en una recibiría un tipo
 * con la misma cara que quien coincide en cuatro. Un reactivo aislado no es
 * diagnóstico —ningún reactivo lo es— así que la puntuación total no puede ser
 * la única condición.
 *
 * Se cuentan situaciones, NO desempates: el desempate sólo se hace cuando el par
 * ya está abierto, así que dejarlo contar aquí permitiría llegar a dos con una
 * situación y la pregunta que se hizo precisamente porque no había suficiente.
 */
const MIN_SITUATIONS_SUPPORTING_LEADER = 2;

/**
 * Además de dos situaciones, hace falta que AL MENOS UNA de ellas sea una señal
 * motivacional fuerte, no dos indicios débiles.
 *
 * La escala de calibración reserva +2 para «evidencia motivacional directa» y
 * +1 para «señal compatible, compartida o principalmente conductual». Sin esta
 * condición, dos respuestas ambiguas del mismo tipo de señal —las que apuntan a
 * dos patrones a la vez— podrían adjudicar un patrón entre las dos. Eso sería
 * clasificar por acumulación de ambigüedad.
 */
const MIN_STRONG_SIGNAL = 2;
const OPEN_MARGIN = 2;
const STRONG_MARGIN = 5;
const MAX_COUNTEREVIDENCE_HITS_BEFORE_OPEN = 2;

export const DIFFERENTIATION_TRIGGER_MARGIN = 3;
export const MAX_DIFFERENTIATION_QUESTIONS = 3;

interface QuestionBank {
  situations: Situation[];
  differentiationQuestions: DifferentiationQuestion[];
}

function findOption(
  answer: Answer,
  bank: QuestionBank,
): {
  option: Option;
  contentVersion: string;
  isDifferentiator: boolean;
  maxInfluence: number;
  candidatePair: [PatternId, PatternId] | null;
} | null {
  for (const situation of bank.situations) {
    if (situation.id !== answer.questionId) continue;
    const option = situation.options.find((o) => o.id === answer.optionId);
    if (option) {
      return {
        option,
        contentVersion: situation.version,
        isDifferentiator: false,
        maxInfluence: MAX_SINGLE_OPTION_WEIGHT,
        candidatePair: null,
      };
    }
  }
  for (const q of bank.differentiationQuestions) {
    if (q.id !== answer.questionId) continue;
    const option = q.options.find((o) => o.id === answer.optionId);
    // `maxInfluence` estaba declarado en el tipo y no lo leía nadie. Un
    // desempate que puede pesar tanto como una situación deja de desempatar y
    // empieza a inclinar, así que el contenido declara su techo y aquí se
    // aplica de verdad.
    if (option) {
      return {
        option,
        contentVersion: q.version,
        isDifferentiator: true,
        maxInfluence: q.maxInfluence,
        candidatePair: q.candidatePair,
      };
    }
  }
  return null;
}

interface DifferentiationSignal {
  questionId: string;
  candidatePair: [PatternId, PatternId];
  supportedPattern: PatternId;
  weight: number;
}

interface ScoreTally {
  scores: Partial<Record<PatternId, number>>;
  /** Scores aportados sólo por situaciones. La decisión diferencial necesita
   *  distinguir evidencia base de la pregunta que intenta aclararla. */
  situationScores: Partial<Record<PatternId, number>>;
  counterHits: Partial<Record<PatternId, number>>;
  /** Por patrón, los ids de SITUACIÓN que le aportaron peso positivo. Set, no
   *  contador: dos opciones de la misma situación no son dos evidencias. */
  supportingSituations: Partial<Record<PatternId, Set<string>>>;
  /** Por patrón, si alguna SITUACIÓN le dio una señal fuerte (+2 o más). */
  hasStrongSignal: Partial<Record<PatternId, boolean>>;
  evidence: string[];
  counterevidence: string[];
  trace: AnswerTrace[];
  differentiationSignals: DifferentiationSignal[];
  /** Número de respuestas válidas a situaciones. Los desempates no pueden
   * completar el mínimo de evidencia base que autoriza una hipótesis. */
  validSituationAnswers: number;
  contentVersion: string;
}

/**
 * Convierte la entrada en el estado actual de respuestas: una sola respuesta
 * por pregunta y gana la última selección declarada.
 *
 * La capa de sesión ya mantiene esta invariante, pero el motor también debe
 * defenderla. De lo contrario, una sesión antigua/corrupta o cualquier otro
 * consumidor de classify() podría repetir una situación hasta fabricar
 * puntuación, cobertura y el mínimo de respuestas.
 *
 * Se canonicaliza ANTES de validar la opción. Si la última selección no existe
 * en el banco, la pregunta queda sin respuesta; no se rescata silenciosamente
 * una selección anterior que el usuario ya había sustituido.
 */
function canonicalizeAnswers(answers: Answer[]): Answer[] {
  const currentByQuestion = new Map<string, Answer>();
  for (const answer of answers) {
    currentByQuestion.delete(answer.questionId);
    currentByQuestion.set(answer.questionId, answer);
  }
  return [...currentByQuestion.values()];
}

function tally(answers: Answer[], bank: QuestionBank): ScoreTally {
  const scores: Partial<Record<PatternId, number>> = {};
  const situationScores: Partial<Record<PatternId, number>> = {};
  const counterHits: Partial<Record<PatternId, number>> = {};
  const supportingSituations: Partial<Record<PatternId, Set<string>>> = {};
  const hasStrongSignal: Partial<Record<PatternId, boolean>> = {};
  const evidence: string[] = [];
  const counterevidence: string[] = [];
  const trace: AnswerTrace[] = [];
  const differentiationSignals: DifferentiationSignal[] = [];
  let validSituationAnswers = 0;
  let contentVersion = "unknown";

  for (const answer of canonicalizeAnswers(answers)) {
    const found = findOption(answer, bank);
    if (!found) continue; // unresolvable answer: does not count as valid
    if (!found.isDifferentiator) validSituationAnswers += 1;
    const { option } = found;
    contentVersion = found.contentVersion;
    const techo = Math.min(found.maxInfluence, MAX_SINGLE_OPTION_WEIGHT);

    const weightChanges: Partial<Record<PatternId, number>> = {};
    for (const [patternId, weight] of Object.entries(option.patternWeights)) {
      if (weight === undefined) continue;
      const clamped = Math.max(-techo, Math.min(techo, weight));
      scores[patternId] = (scores[patternId] ?? 0) + clamped;
      if (!found.isDifferentiator) {
        situationScores[patternId] = (situationScores[patternId] ?? 0) + clamped;
      }
      weightChanges[patternId] = clamped;
      if (clamped < 0) {
        counterHits[patternId] = (counterHits[patternId] ?? 0) + 1;
      }
      if (clamped > 0 && !found.isDifferentiator) {
        (supportingSituations[patternId] ??= new Set()).add(answer.questionId);
        if (clamped >= MIN_STRONG_SIGNAL) hasStrongSignal[patternId] = true;
      }
    }

    // Una pregunta diferencial fuerte puede resolver un margen estrecho, pero
    // sólo si su opción apunta de forma inequívoca a UNO de los dos candidatos.
    // No cuenta como situación ni como señal fuerte base.
    if (found.isDifferentiator && found.candidatePair) {
      const fuertesDelPar = Object.entries(weightChanges).filter(
        ([patternId, weight]) =>
          found.candidatePair?.includes(patternId) &&
          (weight ?? 0) >= MIN_STRONG_SIGNAL,
      );
      if (fuertesDelPar.length === 1) {
        const [supportedPattern, weight] = fuertesDelPar[0]!;
        differentiationSignals.push({
          questionId: answer.questionId,
          candidatePair: found.candidatePair,
          supportedPattern,
          weight: weight ?? 0,
        });
      }
    }

    evidence.push(...option.evidenceTags);
    counterevidence.push(...option.counterevidenceTags);

    trace.push({
      questionId: answer.questionId,
      optionId: answer.optionId,
      signalsAdded: option.signals,
      evidenceAdded: option.evidenceTags,
      counterevidenceAdded: option.counterevidenceTags,
      weightChanges,
      timestamp: Date.now(),
      contentVersion: found.contentVersion,
      engineVersion: ENGINE_VERSION,
    });
  }

  return {
    scores, situationScores, counterHits, supportingSituations, hasStrongSignal,
    evidence, counterevidence, trace, differentiationSignals,
    validSituationAnswers, contentVersion,
  };
}

function rankPatterns(
  scores: Partial<Record<PatternId, number>>,
  patternIds: PatternId[],
): PatternId[] {
  return [...patternIds].sort((a, b) => (scores[b] ?? 0) - (scores[a] ?? 0));
}

/**
 * Un desempate sólo es legítimo cuando la evidencia de SITUACIONES ya deja
 * exactamente dos hipótesis plausibles. Cada candidata debe superar por sí
 * misma las mismas puertas mínimas que necesitaría para poder adjudicarse:
 * puntuación, dos situaciones distintas, una señal fuerte y menos de dos
 * impactos de contraevidencia.
 *
 * Esta función es la única fuente de verdad para preguntar y para adjudicar.
 * Así no puede ocurrir que la interfaz ofrezca una pregunta que el clasificador
 * luego no está autorizado a usar.
 */
function eligibleDifferentiationPair(
  scoreTally: ScoreTally,
  patternIds: PatternId[],
): [PatternId, PatternId] | null {
  if (
    scoreTally.validSituationAnswers < MIN_VALID_ANSWERS ||
    patternIds.length < 2
  ) {
    return null;
  }

  const ranked = rankPatterns(scoreTally.situationScores, patternIds);
  const [baseLeader] = ranked;
  if (!baseLeader) return null;
  const topScore = scoreTally.situationScores[baseLeader] ?? 0;

  const plausible = ranked.filter((patternId) => {
    const score = scoreTally.situationScores[patternId] ?? 0;
    return (
      topScore - score <= DIFFERENTIATION_TRIGGER_MARGIN &&
      score >= MIN_LEADER_SCORE &&
      (scoreTally.supportingSituations[patternId]?.size ?? 0) >=
        MIN_SITUATIONS_SUPPORTING_LEADER &&
      Boolean(scoreTally.hasStrongSignal[patternId]) &&
      (scoreTally.counterHits[patternId] ?? 0) <
        MAX_COUNTEREVIDENCE_HITS_BEFORE_OPEN
    );
  });

  // Si el líder base no supera las puertas mínimas, no se elige un par
  // secundario ignorándolo. Y si hay tres candidatas plausibles, una pregunta
  // binaria no representa la incertidumbre real.
  if (plausible[0] !== baseLeader || plausible.length !== 2) return null;
  return [plausible[0], plausible[1]!];
}

// Coincidencia EXACTA, no por sufijo. Antes era `endsWith("1")`, que servía
// mientras los ids de prueba eran FAKE_1/FAKE_9 — pero con ids reales habría
// hecho que un futuro patrón "11" o "21" se colara en el hueco del 1 sin que
// nadie lo notara. Un mapa explícito falla ruidosamente al añadir un tipo, que
// es exactamente lo que se quiere: obliga a añadir también su estado.
const STATE_BY_PATTERN: Readonly<Record<string, ResultState>> = {
  "1": "pattern_1", "2": "pattern_2", "3": "pattern_3",
  "4": "pattern_4", "5": "pattern_5", "6": "pattern_6",
  "7": "pattern_7", "8": "pattern_8", "9": "pattern_9",
};

function stateFor(patternId: PatternId): ResultState {
  const state = STATE_BY_PATTERN[patternId];
  if (!state) {
    throw new Error(
      `classify: pattern "${patternId}" has no result state. ` +
        "Añade su entrada en STATE_BY_PATTERN y en ResultState antes de activarlo.",
    );
  }
  return state;
}

/**
 * Una respuesta diferencial fuerte puede romper OPEN_MARGIN sin inflar su
 * peso, pero no puede inventar evidencia base ni resolver una ambigüedad de
 * tres o más patrones.
 *
 * La condición es deliberadamente simétrica: no pregunta quién lideraba antes,
 * sino si la opción elegida apoya al líder FINAL y si ese apoyo consiguió una
 * ventaja positiva sobre el otro candidato. Así el segundo puede convertirse
 * en ganador cuando la respuesta realmente invierte el orden.
 */
function hasDecisiveDifferentiation(
  leader: PatternId,
  runnerUp: PatternId,
  margin: number,
  eligiblePair: [PatternId, PatternId] | null,
  differentiationSignals: DifferentiationSignal[],
): boolean {
  if (margin <= 0 || !eligiblePair) return false;

  const finalPair = new Set<PatternId>([leader, runnerUp]);
  const basePair = new Set<PatternId>(eligiblePair);

  return differentiationSignals.some(
    (signal) =>
      signal.supportedPattern === leader &&
      signal.weight >= MIN_STRONG_SIGNAL &&
      signal.candidatePair.every(
        (patternId) => finalPair.has(patternId) && basePair.has(patternId),
      ),
  );
}

/**
 * Pure, deterministic. Same answers + same content => same result, always.
 * Call this after situations alone to decide (in the session/state layer)
 * whether a differentiation question is needed, and again after any
 * differentiation answers are added, to get the final result.
 */
export function classify(
  answers: Answer[],
  bank: QuestionBank,
  // Antes era [PatternId, PatternId]. La LÓGICA nunca fue binaria —
  // `rankPatterns` ordena N patrones por puntuación y toma los dos primeros—
  // así que la tupla sólo era una restricción de tipo heredada del alfa de dos.
  // Se abre a N para que el alfa pueda ser 1-6-9 sin tocar el algoritmo, y para
  // que llegar a nueve no sea una reescritura. Con menos de dos no hay líder ni
  // segundo, y eso es un error de programación, no un estado del usuario.
  patternIds: PatternId[],
): ClassificationResult {
  const scoreTally = tally(answers, bank);
  const {
    scores, counterHits, supportingSituations, hasStrongSignal,
    evidence, counterevidence, trace, differentiationSignals,
    validSituationAnswers, contentVersion,
  } = scoreTally;

  const base = {
    decisionTrace: trace,
    evidence: [...new Set(evidence)],
    counterevidence: [...new Set(counterevidence)],
    engineVersion: ENGINE_VERSION,
    contentVersion,
  };

  if (validSituationAnswers < MIN_VALID_ANSWERS) {
    return {
      ...base,
      state: "insufficient",
      consistency: "insuficiente",
      primaryHypothesis: null,
      alternativeHypothesis: null,
      differentiator: null,
      reasonForOpenState: "Fewer valid answers than are needed to put forward a hypothesis.",
    };
  }

  if (patternIds.length < 2) throw new Error("classify: need at least two patterns");
  const [leader, runnerUp] = rankPatterns(scores, patternIds);
  if (!leader || !runnerUp) {
    throw new Error("classify: expected exactly two candidate patterns");
  }
  const leaderScore = scores[leader] ?? 0;
  const margin = leaderScore - (scores[runnerUp] ?? 0);
  const leaderCounterHits = counterHits[leader] ?? 0;

  const situacionesQueApoyan = supportingSituations[leader]?.size ?? 0;

  if (
    leaderScore < MIN_LEADER_SCORE ||
    situacionesQueApoyan < MIN_SITUATIONS_SUPPORTING_LEADER
  ) {
    return {
      ...base,
      state: "insufficient",
      consistency: "insuficiente",
      primaryHypothesis: null,
      alternativeHypothesis: null,
      differentiator: null,
      reasonForOpenState: "The signals gathered are too weak or too contradictory to support any hypothesis.",
    };
  }

  // La señal fuerte condiciona EMITIR UN PATRÓN, no llegar hasta aquí. Un líder
  // sostenido sólo por indicios de +1 —los compartidos o principalmente
  // conductuales— tiene evidencia real, así que decir `insufficient` sería
  // negar lo que sí se recogió; pero tampoco da para adjudicar. Eso es
  // exactamente `open`: hay hipótesis, ninguna es decisiva.
  const eligiblePair = eligibleDifferentiationPair(scoreTally, patternIds);
  const decisiveDifferentiation = hasDecisiveDifferentiation(
    leader,
    runnerUp,
    margin,
    eligiblePair,
    differentiationSignals,
  );
  const marginRemainsOpen =
    margin <= OPEN_MARGIN && !decisiveDifferentiation;

  if (
    marginRemainsOpen ||
    leaderCounterHits >= MAX_COUNTEREVIDENCE_HITS_BEFORE_OPEN ||
    !hasStrongSignal[leader]
  ) {
    const reason = !hasStrongSignal[leader]
      ? "The hypothesis rests only on shared or behavioural signals; no answer points to it directly."
      : leaderCounterHits >= MAX_COUNTEREVIDENCE_HITS_BEFORE_OPEN
        ? "The counterevidence gathered weakens the leading hypothesis enough not to force a winner."
        : "Both hypotheses hold comparable evidence; neither separates clearly.";
    return {
      ...base,
      state: "open",
      consistency: "abierto",
      primaryHypothesis: leader,
      alternativeHypothesis: runnerUp,
      differentiator: findDifferentiatorId(trace, bank),
      reasonForOpenState: reason,
    };
  }

  const consistency: ConsistencyLevel =
    leaderCounterHits >= 1
      ? "moderada" // counterevidence caps consistency even with a wide margin
      : margin >= STRONG_MARGIN
        ? "alta"
        : "moderada";

  return {
    ...base,
    state: stateFor(leader),
    consistency,
    primaryHypothesis: leader,
    alternativeHypothesis: runnerUp,
    differentiator: findDifferentiatorId(trace, bank),
    reasonForOpenState: null,
  };
}

function findDifferentiatorId(trace: AnswerTrace[], bank: QuestionBank): string | null {
  const diffIds = new Set(bank.differentiationQuestions.map((q) => q.id));
  const answered = trace.find((t) => diffIds.has(t.questionId));
  return answered?.questionId ?? null;
}

/**
 * QUÉ desempate toca, no sólo si toca alguno.
 *
 * `needsDifferentiation` sólo dice que el margen es estrecho. Quien llamaba
 * elegía después la pregunta a mano —`main.ts` tenía clavado el desempate 1↔9—
 * así que con dos desempates reales en el banco, un empate 9↔6 habría recibido
 * la pregunta del par 1↔6: una pregunta cuyas dos opciones puntúan al 1 y al 6,
 * hecha a alguien cuyo par abierto no incluye al 1. Eso no desempata: mete peso
 * en un patrón que no estaba en discusión.
 *
 * Aquí se elige por `candidatePair` contra el par realmente abierto, sin
 * importar el orden, y se saltan los ya contestados — que además cierra el otro
 * agujero: volver a programar la misma pregunta hasta agotar el máximo.
 *
 * Devuelve `null` cuando no hay desempate para ese par. Es una respuesta válida
 * y significa terminar: el par se queda `open`, que es honesto. Inventar un
 * desempate para un par que el material no trata sería peor que no tenerlo.
 */
export function differentiatorFor(
  answers: Answer[],
  bank: QuestionBank,
  patternIds: PatternId[],
): DifferentiationQuestion | null {
  const scoreTally = tally(answers, bank);
  const eligiblePair = eligibleDifferentiationPair(scoreTally, patternIds);
  if (!eligiblePair) return null;
  const abierto = new Set<PatternId>(eligiblePair);
  const contestadas = new Set(scoreTally.trace.map((a) => a.questionId));
  return (
    bank.differentiationQuestions.find(
      (q) =>
        !contestadas.has(q.id) &&
        q.candidatePair.length === abierto.size &&
        q.candidatePair.every((p) => abierto.has(p)),
    ) ?? null
  );
}

/**
 * Used by the session layer to decide, after situations alone, whether a
 * differentiation question should be asked before finalizing.
 */
export function needsDifferentiation(
  answers: Answer[],
  bank: QuestionBank,
  // Antes era [PatternId, PatternId]. La LÓGICA nunca fue binaria —
  // `rankPatterns` ordena N patrones por puntuación y toma los dos primeros—
  // así que la tupla sólo era una restricción de tipo heredada del alfa de dos.
  // Se abre a N para que el alfa pueda ser 1-6-9 sin tocar el algoritmo, y para
  // que llegar a nueve no sea una reescritura. Con menos de dos no hay líder ni
  // segundo, y eso es un error de programación, no un estado del usuario.
  patternIds: PatternId[],
  differentiationAsked: number,
): boolean {
  if (differentiationAsked >= MAX_DIFFERENTIATION_QUESTIONS) return false;
  return eligibleDifferentiationPair(tally(answers, bank), patternIds) !== null;
}
